/**
 * Contextual page help for CareerSavvy — the CareerSavvy counterpart of
 * ElevateStaffing's ModuleHelper/helperData. Each entry powers the "?" info
 * button next to a page title (see components/InfoHelper.jsx).
 *
 * Keyed by route path so it lines up with the SUBTITLES map in AppShell.
 * Shape per entry:
 *   title      – heading shown in the popover
 *   shortTip   – one or two sentences: what this page is for
 *   features   – optional bullet list, "**Label** — description" strings
 *   proTip     – optional highlighted tip
 */

const HELP_CONTENT = {
  "/dashboard": {
    title: "Dashboard",
    shortTip:
      "Your job-search home base. See your top matches, recent activity, and what to do next at a glance.",
    features: [
      "**Top matches** — Roles scored against your resume so you can act on the best fits first",
      "**Activity** — A running log of applications, interviews, and resume updates",
      "**Next steps** — Suggested actions to keep your search moving",
    ],
    proTip: "Check in here first each day — it surfaces what needs your attention now.",
  },
  "/career-journey": {
    title: "Career Journey",
    shortTip:
      "Talk to your AI coach to set a target role, get a step-by-step plan, and track your progress toward it.",
    features: [
      "**Target role** — Pick where you want to go; the plan is built around it",
      "**Roadmap** — A sequence of steps (skills, projects, applications) with a timeline",
      "**Progress** — Mark steps done and watch your readiness score climb",
    ],
    proTip: "Be specific with your coach about your target role and timeline — the plan gets sharper.",
  },
  "/search-job": {
    title: "Jobs",
    shortTip:
      "Browse live roles matched to your resume. Search, filter, and apply — or tailor your resume to a role first.",
    features: [
      "**Matched to you** — Jobs ranked by how well they fit your resume",
      "**Filters** — Narrow by keyword, location, work type (intern / full-time / contract / remote), and recency",
      "**Match a job** — Jump to a fit analysis and tailored resume for any role",
    ],
    proTip: "Use the filters to cut the list down, then open a role to see your match details before applying.",
  },
  "/job-match": {
    title: "Match a job",
    shortTip:
      "Paste any job description to see how your resume matches it — and exactly what to change to score higher.",
    features: [
      "**Match score** — How closely your resume aligns with the role",
      "**Gaps** — Missing skills and keywords the role is asking for",
      "**Tailored resume** — Generate a version of your resume aimed at this specific job",
    ],
    proTip: "Tailor your resume per role before applying — a targeted resume beats a generic one every time.",
  },
  "/recording": {
    title: "Mock interview",
    shortTip:
      "Practice a live interview with an AI co-pilot. Answer out loud in real time and get instant guidance.",
    features: [
      "**Live practice** — A real-time back-and-forth, like a real interview",
      "**Co-pilot** — On-the-spot hints and structure while you answer",
      "**Voice-first** — Speak your answers; no typing required",
    ],
    proTip: "Treat it like the real thing — speak your full answer out loud rather than reading notes.",
  },
  "/interview-prep": {
    title: "Interview prep",
    shortTip:
      "Rehearse behavioral interview questions by text or video, then get an AI score and detailed feedback.",
    features: [
      "**Mock interview** — Attempt questions in text or on video",
      "**Scores** — Overall, highest, and lowest ratings across your answers",
      "**Feedback** — Specific strengths and areas to improve",
    ],
    proTip: "Record a video attempt at least once — reviewing your delivery is where the biggest gains are.",
  },
  "/aspire-quest": {
    title: "Aspire Quest",
    shortTip:
      "Test and sharpen your skills with timed assessments, including hands-on coding exercises in an editor.",
    features: [
      "**Assessments** — Skill and knowledge exams tailored to your track",
      "**Coding editor** — Solve coding questions in an in-browser editor",
      "**History** — Revisit past attempts and see how you've improved",
    ],
    proTip: "Run the system check before you start so your mic and setup are ready for the session.",
  },
  "/resume-upload": {
    title: "Resume",
    shortTip:
      "Upload your resume and keep your profile details current — everything else in CareerSavvy builds on this.",
    features: [
      "**Upload** — Add or replace your resume; matches and tailoring use it",
      "**Details** — Keep your contact info and profile fields up to date",
      "**Save** — Save your details so they carry across the app",
    ],
    proTip: "Keep your resume fresh — job matches and tailored versions are only as good as what's on file.",
  },
  "/outreach": {
    title: "Outreach",
    shortTip:
      "Run personalised email campaigns from your own mailbox — upload a contact list once and reach out at scale.",
    features: [
      "**Your mailbox** — Emails send from your own connected inbox, not a shared address",
      "**Contact lists** — Upload recipients once and reuse them",
      "**Personalised** — Tailor each message so it doesn't read like a blast",
    ],
    proTip: "Personalise the opening line for each contact — reply rates jump when it doesn't feel like a mass email.",
  },
  "/profile": {
    title: "Profile",
    shortTip: "Manage your account details, contact info, and preferences.",
    features: [
      "**Account details** — Your name, email, and phone",
      "**Preferences** — Settings that personalise your experience",
      "**Save** — Remember to save after editing any field",
    ],
    proTip: "Add a phone number in the right format — it's used across job applications and outreach.",
  },
  "/subscription": {
    title: "Subscription",
    shortTip: "View and manage your plan, billing, and renewal. Upgrade to unlock Pro features.",
    features: [
      "**Plan** — See whether you're on Free or Pro",
      "**Billing** — Manage payment and see your renewal date",
      "**Upgrade** — Unlock match analysis, tailored resumes, and voice interviews",
    ],
    proTip: "Pro unlocks the features that make the biggest difference — match analysis and tailored resumes.",
  },
};

export default HELP_CONTENT;
